// OnboardingStep4.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import { useAccessibility, useAccessibilityTags, useI18N } from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import WideButton from "@/components/common/WideButton";
import CustomCheckbox from "@/components/common/CustomCheckbox";
import { ArrowLeft } from "lucide-react";

interface OnboardingStep4Props {
  title: string;
  subtitle: string;
}

const OnboardingStep4 = ({ title, subtitle }: OnboardingStep4Props) => {
  const i18n = useI18N();
  const isDark = useDarkMode();

  //set index for swipable views
  const { setScreenIndex } = useAuthModals();

  //fetch data options to display
  const { preparedData, tagsToAdd } = useAccessibilityTags();

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);

  useEffect(() => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountAccessibilityFeatureYouRequirePage"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountAccessibilityFeatureYouRequirePage",
      {}
    );
    const selectedString = submitData.accessibilityFeatureRequestTagsValue;
    if (selectedString) {
      const selected = selectedString.split(",").map((s) => parseInt(s));
      setSelectedOptions(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionChange = (option: number) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const onBack = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep4BackButtonClicked"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountStep4BackButtonClicked",
      {}
    );
    setScreenIndex(AuthModalScreen.OnboardingStep3);
  };

  const onSkip = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep4Skip");
    AnalyticsService.trackMx("authPopupCreateAccountStep4Skip", {});
    setScreenIndex(AuthModalScreen.OnboardingStep5);
  };

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep4Completed"
    );
    AnalyticsService.trackMx("authPopupCreateAccountStep4Completed", {});

    setSubmitData((prev) => ({
      ...prev,
      accessibilityFeatureRequestTagsValue: selectedOptions.join(","),
    }));

    //move to next step
    setScreenIndex(AuthModalScreen.OnboardingStep5);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="flex flex-col rounded-xl bg-white dark:bg-primary-900 overflow-y-auto"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {i18n.get("profileSetup")}
        </h1>      
      </header>
      <div className="px-8 md:px-16">
        <h2
          className="mt-6 mb-4 text-primary-900 dark:text-primary-100 desktop-h1-xsmall-extra-bold"
          tabIndex={0}
        >
          {title}
        </h2>
        <p
          className="paragraph-medium-medium text-primary-900 dark:text-primary-100"
          tabIndex={0}
        >
          {subtitle}
        </p>
        <div>
          <hr className="w-full border-primary-150 dark:border-primary-200 my-5" />
        </div>
      </div>
      <div className="flex-1 px-8 md:px-16 overflow-y-scroll">
        <div className="mb-6">
          <p
            className="desktop-h2-medium-semi-bold text-primary-900 dark:text-primary-100 mb-3"
            tabIndex={0}
          >
            {i18n.get("whatAccessibilityFeaturesDoYouRequire")}
          </p>
          <p
            className="paragraph-small-semibold !text-primary-500 dark:!text-primary-300 block"
            tabIndex={0}
          >
            {i18n.get("selectAllThatApply2")}
          </p>
        </div>
        <div className="flex flex-col gap-2.5">
          {preparedData.map((option, index) => {
            if (option.accessibility_tags.length <= 0) return null;
            return (
              <div key={index} className="">
                <h3
                  className="mobile-h3-small-semi-bold text-primary-900 dark:text-primary-100 mb-4"
                  tabIndex={0}
                >
                  {option.name}
                </h3>
                {/* Adjusted for semantic HTML */}
                {option.accessibility_tags.map((o) => (
                  <div key={o.id} className="my-2" role="none">
                    <CustomCheckbox
                      label={o.name}
                      checked={selectedOptions.includes(o.id)}
                      onChange={() => handleOptionChange(o.id)}
                    />
                    {o.id === 428 && selectedOptions.includes(o.id) && (
                      <div key={o.id + "321"} className="ml-8">
                        <h3
                          className="mobile-h3-small-semi-bold text-primary-900 dark:text-primary-100 mt-2"
                          tabIndex={0}
                        >
                          {i18n.get("wouldAnyOfTheseAlsoWork")}
                        </h3>
                        {/* Added text content */}
                        {tagsToAdd.map((tag) => (
                          <div key={tag.id} className="mt-2">
                            <CustomCheckbox
                              label={tag.name}
                              checked={selectedOptions.includes(tag.id)}
                              onChange={() => handleOptionChange(tag.id)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mb-8 items-center justify-center flex flex-col px-8 md:px-16 pt-2">
        <WideButton
          disabled={selectedOptions.length <= 0}
          type="filled"
          onClick={() => onContinue()}
        >
          {i18n.get("continue")}
        </WideButton>
        <button
          onClick={onSkip}
          className="button-small-semi-bold underline text-links dark:text-white mt-6"
        >
          {i18n.get("skipForNow")}
        </button>
      </div>
      <button onClick={onBack} className="absolute top-4 left-4">
          <ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>      
    </div>
  );
};

export default OnboardingStep4;
